import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { Observation, Patient } from 'fhir/r4';
import { ObservationReferenceRangeService, ObservationReferenceRangeTemplate, ReferenceRangeSuggestion } from '../observation-reference-range.service';
import { NotificationService } from '../../shared/notification/notification.service';
import { ErrorService } from '../../shared/error.service';
import { ValueToStringPipe } from '../../shared/value-to-string.pipe';
import { OrganizationSettingsService } from '../../shared/organization-settings.service';

export interface ConceptOption {
  code: string;
  display: string;
  system: string;
}

export interface ReferenceRangeSuggestionDialogData {
  observation: Observation;
  patient?: Patient;
}

export interface ReferenceRangeSuggestionDialogResult {
  updatedObservation?: Observation;
  appliedTemplate?: ObservationReferenceRangeTemplate;
}

@Component({
  selector: 'app-reference-range-suggestion-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatChipsModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatDividerModule,
    MatTabsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    ValueToStringPipe
  ],
  templateUrl: './reference-range-suggestion-dialog.component.html',
  styleUrl: './reference-range-suggestion-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObservationReferenceRangeSuggestionDialogComponent implements OnInit {
  private readonly dialogRef = inject<
    MatDialogRef<ObservationReferenceRangeSuggestionDialogComponent, ReferenceRangeSuggestionDialogResult>
  >(MatDialogRef);
  private readonly dialogData = inject<ReferenceRangeSuggestionDialogData>(MAT_DIALOG_DATA);
  private readonly referenceRanges = inject(ObservationReferenceRangeService);
  private readonly notifications = inject(NotificationService);
  private readonly errorService = inject(ErrorService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly orgSettings = inject(OrganizationSettingsService);

  canManageManual$ = this.orgSettings.canManageManualReferenceRanges();

  readonly observation = this.dialogData.observation;
  readonly patient = this.dialogData.patient;

  suggestions: ReferenceRangeSuggestion[] = [];
  loading = false;
  applyInFlightKey: string | null = null;
  manualSaving = false;

  manualForm = this.fb.group({
    lowValue: [null as number | null],
    highValue: [null as number | null],
    meaning: [null as string | null],
    interpretation: [null as string | null]
  });

  readonly rangeTypeOptions: ConceptOption[] = [
    { code: 'normal', display: 'Normal range', system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning' },
    { code: 'treatment', display: 'Critical / Needs Treatment', system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning' },
    { code: 'recommended', display: 'Recommended range', system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning' },
    { code: 'therapeutic', display: 'Therapeutic desired level', system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning' }
  ];

  readonly interpretationOptions: ConceptOption[] = [
    { code: 'N', display: 'Normal (N)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' },
    { code: 'H', display: 'High (H)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' },
    { code: 'L', display: 'Low (L)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' },
    { code: 'HH', display: 'Critical High (HH)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' },
    { code: 'LL', display: 'Critical Low (LL)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' },
    { code: 'A', display: 'Abnormal (A)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' },
    { code: 'AA', display: 'Critical Abnormal (AA)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' },
    { code: 'POS', display: 'Positive (POS)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' },
    { code: 'NEG', display: 'Negative (NEG)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' },
    { code: 'IND', display: 'Indeterminate (IND)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' },
    { code: 'RR', display: 'Reactive (RR)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' },
    { code: 'NR', display: 'Non-reactive (NR)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' },
    { code: 'S', display: 'Susceptible (S)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' },
    { code: 'R', display: 'Resistant (R)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' },
    { code: 'I', display: 'Intermediate (I)', system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation' }
  ];

  readonly displayedColumns = ['type', 'range', 'appliesTo', 'age', 'classification', 'actions'];

  constructor() {
  }

  ngOnInit(): void {
    this.loadSuggestions();
    this.initManualForm();
  }

  private initManualForm(): void {
    const existingRange = this.observation?.referenceRange?.[0];
    if (existingRange) {
      this.manualForm.patchValue({
        lowValue: existingRange.low?.value ?? null,
        highValue: existingRange.high?.value ?? null,
        meaning: existingRange.type?.coding?.[0]?.code ?? null
      });
    }

    const existingInterp = this.observation?.interpretation?.[0];
    if (existingInterp) {
      this.manualForm.patchValue({
        interpretation: existingInterp.coding?.[0]?.code ?? null
      });
    }
  }

  saveManualRange(): void {
    if (this.manualForm.invalid) return;
    const vals = this.manualForm.value;

    this.manualSaving = true;

    // Build ReferenceRange
    const newRange: any = {};
    const obsVal = this.observation?.valueQuantity;
    if (vals.lowValue != null) {
      newRange.low = { value: vals.lowValue, unit: obsVal?.unit, system: obsVal?.system, code: obsVal?.code };
    }
    if (vals.highValue != null) {
      newRange.high = { value: vals.highValue, unit: obsVal?.unit, system: obsVal?.system, code: obsVal?.code };
    }
    if (vals.meaning) {
      const typeOption = this.rangeTypeOptions.find(o => o.code === vals.meaning);
      if (typeOption) {
        newRange.type = { coding: [{ code: typeOption.code, system: typeOption.system, display: typeOption.display }], text: typeOption.display };
      }
    }

    const nextObs = JSON.parse(JSON.stringify(this.observation)) as Observation;
    
    if (Object.keys(newRange).length > 0) {
      nextObs.referenceRange = [newRange];
    } else {
      nextObs.referenceRange = undefined;
    }

    if (vals.interpretation) {
      const interpOption = this.interpretationOptions.find(o => o.code === vals.interpretation);
      if (interpOption) {
        nextObs.interpretation = [{ coding: [{ code: interpOption.code, system: interpOption.system, display: interpOption.display }], text: interpOption.display }];
      }
    } else {
      nextObs.interpretation = undefined;
    }

    this.referenceRanges.updateObservation(nextObs).pipe(
      finalize(() => {
        this.manualSaving = false;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (updated: any) => {
        this.dialogRef.close({ updatedObservation: updated });
      },
      error: (err: any) => {
        console.error('Failed to apply manual reference range', err);
        this.errorService.openandCloseError('Failed to apply manual reference range.');
      }
    });
  }

  trackByTemplate = (_index: number, template: ReferenceRangeSuggestion): string => {
    return this.makeTemplateKey(template);
  }

  rangeDescription(range?: ObservationReferenceRangeTemplate['range']): string {
    return this.referenceRanges.describeRange(range);
  }

  appliesToDescription(range?: ObservationReferenceRangeTemplate['range']): string {
    return this.referenceRanges.describeAppliesTo(range);
  }

  ageDescription(range?: ObservationReferenceRangeTemplate['range']): string {
    return this.referenceRanges.describeAge(range);
  }

  loadSuggestions(): void {
    if (!this.observation?.code?.coding?.length) {
      this.suggestions = [];
      return;
    }
    this.loading = true;
    this.cdr.markForCheck();
    
    this.referenceRanges
      .matchRangesForObservation(this.observation, this.patient)
      .pipe(finalize(() => {
        this.loading = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: suggestions => {
          this.suggestions = suggestions ?? [];
          this.cdr.markForCheck();
        },
        error: () => {
          this.suggestions = [];
          this.errorService.openandCloseError('Unable to load reference range templates.');
          this.cdr.markForCheck();
        }
      });
  }

  applyRange(template: any): void {
    if (!this.observation?.id) {
      this.errorService.openandCloseError('Save the observation before applying a reference range.');
      return;
    }
    if (!template?.range) {
      this.errorService.openandCloseError('Selected template has no range definition.');
      return;
    }
    const key = this.makeTemplateKey(template);
    this.applyInFlightKey = key;

    const rangeToApply = { ...template.range };
    
    // Attach interpretation based on classification
    if (template.classification && template.workflow === 'normal' && template.classification !== 'unknown') {
      let code = 'N';
      let display = 'Normal';
      if (template.classification === 'high') { code = 'H'; display = 'High'; }
      if (template.classification === 'low') { code = 'L'; display = 'Low'; }

      rangeToApply.interpretation = [{
        coding: [{
          system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
          code: code,
          display: display
        }],
        text: display
      }];
    } else if (template.workflow === 'typed') {
      const typeCoding = template.range?.type?.coding?.[0];
      if (typeCoding && typeCoding.display) {
        rangeToApply.interpretation = [{
          coding: [{
            system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
            code: template.classification || 'unknown',
            display: typeCoding.display
          }],
          text: typeCoding.display
        }];
      }
    }

    this.referenceRanges
      .applyRangeToObservation(this.observation, rangeToApply)
      .pipe(finalize(() => (this.applyInFlightKey = null)))
      .subscribe({
        next: updated => {
          this.notifications.success('Reference range applied to observation.');
          this.dialogRef.close({
            updatedObservation: updated,
            appliedTemplate: template
          });
        },
        error: () => this.errorService.openandCloseError('Failed to apply range. Please try again.')
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  manageTemplates(): void {
    this.router.navigate(['/app/observation-reference-ranges']).then(() => {
      this.close();
    });
  }

  isTemplateApplying(template: ObservationReferenceRangeTemplate | ReferenceRangeSuggestion): boolean {
    return this.applyInFlightKey === this.makeTemplateKey(template);
  }

  classificationLabel(value: ReferenceRangeSuggestion['classification']): string {
    switch (value) {
      case 'low':
        return 'Below range';
      case 'high':
        return 'Above range';
      case 'within':
        return 'Within range';
      default:
        return 'Unknown';
    }
  }

  classificationIcon(value: ReferenceRangeSuggestion['classification']): string {
    switch (value) {
      case 'low':
        return 'south';
      case 'high':
        return 'north';
      case 'within':
        return 'check_circle';
      default:
        return 'help';
    }
  }

  observationDisplay(): string {
    const code = this.observation?.code;
    if (!code) {
      return 'Observation';
    }
    return code.text || code.coding?.[0]?.display || code.coding?.[0]?.code || 'Observation';
  }

  observationCode(): string {
    const coding = this.observation?.code?.coding?.[0];
    if (!coding) {
      return '';
    }
    return [coding.system, coding.code].filter(Boolean).join(' | ');
  }

  canApplyRanges(): boolean {
    return !!this.observation?.id;
  }

  private makeTemplateKey(template: ObservationReferenceRangeTemplate | ReferenceRangeSuggestion): string {
    return (
      template.id ||
      `${template.code?.system || 'local'}|${template.code?.code || crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`
    );
  }
}
